import { useState, useCallback, useEffect, useRef } from "react";
import LoadingSpinner from "../../../components/LoadingSpinner";
import {
  getAttendees,
  SearchEventsByParams,
} from "../../../services/EventsService";
import { useAxiosInstance } from "../../../services/Interceptor";
import Attendees from "./attendees";
import { useDispatch } from "react-redux";
import { setAttendees } from "../../../store/slices/eventSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faSearch } from "@fortawesome/free-solid-svg-icons";

export default function Events({ handleAttendeesClick, profileUpdateTrigger }) {
  const [loading, setLoading] = useState(false);
  const [loadingAttendees, setLoadingAttendees] = useState(false);
  const [attendeesEvent, setAttendeesEvent] = useState([]);
  const apiInstance = useAxiosInstance();
  const [error, setError] = useState(null);
  const [eventIdSelected, setEventIdSelected] = useState();
  const [eventNameSelected, setEventNameSelected] = useState();
  const [events, setEvents] = useState([]);
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);

  const normalize = (value) =>
    String(value ?? "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const filteredItems = events.filter((item) => {
    const haystack = normalize([item.date, item.name, item.city].join(" "));
    const tokens = normalize(search).split(/\s+/).filter(Boolean);
    return tokens.every((t) => haystack.includes(t));
  });

  const fetchAttendees = useCallback(
    async (eventId) => {
      setLoadingAttendees(true);
      try {
        const data = await getAttendees(apiInstance, eventId);
        setAttendeesEvent(data);
      } catch (err) {
        setError(err.message || "An error occurred while fetching attendees");
      } finally {
        setLoadingAttendees(false);
      }
    },
    [apiInstance],
  );

  useEffect(() => {
    const loadEvents = async () => {
      setLoading(true);
      dispatch(setAttendees({ eventId: undefined, userAthlete: undefined }));
      try {
        const data = await SearchEventsByParams(apiInstance, "");
        setEvents(data);
      } catch (err) {
        setError(err.message || "An error occurred while fetching events");
      } finally {
        setLoading(false);
      }
    };
    loadEvents();
  }, [apiInstance, dispatch]);

  useEffect(() => {
    if (eventIdSelected) {
      fetchAttendees(eventIdSelected);
    }
  }, [eventIdSelected, fetchAttendees, profileUpdateTrigger]);
  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleSearchEventChange = (eventId) => {
    if (eventId) {
      dispatch(setAttendees({ eventId: undefined, userAthlete: undefined }));
      fetchAttendees(eventId);
      setEventIdSelected(eventId);
      var selectedEvent = events.filter(function (item) {
        return item.id === eventId;
      });
      setEventNameSelected(
        `${selectedEvent[0].date} - ${selectedEvent[0].name} - ${selectedEvent[0].city}`,
      );
    } else {
      setAttendeesEvent([]);
    }
  };

  const truncateName = (text) => {
    const fullText = `${text}`;
    return fullText.length > 75 ? fullText.slice(0, 72) + "..." : fullText;
  };

  return (
    <>
      <div className="bg-white shadow-lg rounded-lg border-gray-300">
        <div className="w-full px-4">
          <h1 className="text-xl mb-3 uppercase pt-3">Combines</h1>
          {error && (
            <div className="bg-red-300 text-red-800 border border-red-400 p-2">
              {error}
            </div>
          )}
          {loading ? (
            <LoadingSpinner />
          ) : (
            <form className="w-full mx-auto mb-5">
              <label
                htmlFor="event"
                className="block mb-2 text-sm font-normal text-gray-600"
              >
                Select Event
              </label>

              <div className="relative inline-block text-left w-full">
                <button
                  type="button"
                  onClick={() => setIsOpen(!isOpen)}
                  className="w-full p-2 text-left border border-gray-300 rounded-md focus:outline-none focus:ring focus:border-blue-400 appearance-none"
                >
                  {eventNameSelected || "Select Event"}{" "}
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className="absolute top-3 right-3"
                  />
                </button>

                {isOpen && (
                  <div
                    ref={dropdownRef}
                    className="absolute mt-2 bg-white rounded-md shadow z-10 w-full border border-gray-300"
                  >
                    <div className="p-2 relative">
                      <span className="absolute left-5 top-3.5 text-gray-400">
                        <FontAwesomeIcon icon={faSearch} />
                      </span>
                      <input
                        type="text"
                        placeholder="Search event name..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-10 pr-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring focus:border-blue-400"
                      />
                    </div>
                    <ul className="max-h-80 overflow-y-auto">
                      {filteredItems.length > 0 ? (
                        filteredItems.map((item, index) => (
                          <li
                            key={index}
                            onClick={() => {
                              handleSearchEventChange(item.id);
                              setIsOpen(false);
                              setSearch("");
                            }}
                            className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                          >
                            {truncateName(
                              `${item.date} ${item.name} ${item.city}`,
                            )}
                          </li>
                        ))
                      ) : (
                        <li className="px-4 py-2 text-gray-500">
                          No results found
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </form>
          )}
        </div>
        {loadingAttendees ? (
          <div className="mt-10 mb-20">
            <LoadingSpinner />
          </div>
        ) : (
          <Attendees
            attendees={attendeesEvent}
            eventId={eventIdSelected}
            eventName={eventNameSelected}
            handleAttendeesClick={handleAttendeesClick}
          />
        )}
      </div>
    </>
  );
}
